import { useMemo, type ReactNode } from "react";
import { StyleSheet, View } from "react-native";

import { space } from "@passfirst/ui";

import { type Colors, useColors } from "../theme";
import { Button } from "./button";

interface Props {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  /** Optional helper rendered above the button (e.g. an inline note). */
  children?: ReactNode;
  /** Accessibility hint passed straight to the Pressable. */
  accessibilityLabel?: string;
  testID?: string;
  /**
   * Cap the button to the same width as the screen's content column and
   * center it, so the footer aligns with the capped content above it
   * instead of stretching the full pane width. Pass the same value used
   * for BrandSplit's `contentWidth`.
   */
  contentWidth?: number;
}

// Sticky coral CTA pinned to the bottom of the diagnostic / paywall /
// onboarding screens. The state picker, welcome screen, diagnostic,
// and paywall all share this exact footer shape.
export function PinnedCtaFooter({
  label,
  onPress,
  disabled,
  children,
  accessibilityLabel,
  testID,
  contentWidth,
}: Props): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const column =
    contentWidth != null
      ? { width: contentWidth, maxWidth: "100%" as const, alignSelf: "center" as const }
      : null;
  return (
    <View style={styles.footer}>
      {children}
      <Button
        label={label}
        onPress={onPress}
        disabled={disabled}
        accessibilityLabel={accessibilityLabel ?? label}
        testID={testID}
        fullWidth={column == null}
        style={column ?? undefined}
      />
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    footer: {
      paddingHorizontal: space[6],
      paddingTop: space[4],
      paddingBottom: space[8],
      borderTopWidth: 1,
      borderTopColor: colors.borderSoft,
      backgroundColor: colors.surface,
      gap: space[2],
    },
  });
}
